// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FolderOpen, 
  Upload, 
  FileText, 
  HardDrive,
  BookOpen,
  Wrench,
  Clock,
  ArrowRight,
  Folder,
  TrendingUp,
  Users,
  RefreshCw
} from 'lucide-react';
import UploadModal from '../components/UploadModal';
import To62ReminderBanner from '../components/To62ReminderBanner';
import { formatFileSize, formatDate, getFileIcon } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const canWriteDocs = isAdmin() || ['servo_contas', 'secretario'].includes(user?.role);
  const [stats, setStats] = useState(null);
  const [recentFiles, setRecentFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  
  // Get selected area
  const selectedArea = localStorage.getItem('selectedArea') || 'congregacao';
  const isCongrengacao = selectedArea === 'congregacao';
  
  const areaConfig = {
    congregacao: {
      name: 'Congregação',
      fullName: 'Contas da Congregação',
      icon: BookOpen,
      color: 'text-congregation',
      bgColor: 'bg-congregation-soft',
      borderColor: 'border-congregation-border',
      statKey: 'congregation_files'
    },
    manutencao: {
      name: 'Manutenção',
      fullName: 'Contas da Manutenção',
      icon: Wrench,
      color: 'text-maintenance',
      bgColor: 'bg-maintenance-soft',
      borderColor: 'border-maintenance-border',
      statKey: 'maintenance_files'
    }
  };
  
  const currentArea = areaConfig[selectedArea];
  const AreaIcon = currentArea.icon;

  const fetchData = async () => {
    try {
      const [statsRes, filesRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/files?category=${selectedArea}`)
      ]);
      setStats(statsRes.data);
      setRecentFiles(filesRes.data.files.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('selectedArea')) {
      navigate('/select-area');
      return;
    }
    fetchData();
    // fetchData/navigate are stable per render; re-fetch only when the area changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArea]);

  const handleUploadSuccess = () => {
    fetchData();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-muted-foreground animate-spin" />
        </div>
      </Layout>
    );
  }

  const areaFiles = stats?.[currentArea.statKey] || 0;
  const areaFolders = stats?.total_folders || 0;

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header with gradient accent */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 dark:from-slate-800 dark:via-slate-900 dark:to-black p-6 sm:p-8 animate-fade-in">
          <div className="absolute inset-0 bg-grid-white/5" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-xl ${isCongrengacao ? 'bg-blue-500/20' : 'bg-green-500/20'} flex items-center justify-center`}>
                  <AreaIcon className={`w-5 h-5 ${isCongrengacao ? 'text-blue-400' : 'text-green-400'}`} />
                </div>
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  {currentArea.name}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white font-heading">
                Bem-vindo, {user?.name?.split(' ')[0]}
              </h1>
              <p className="text-slate-400 mt-1 text-sm sm:text-base">
                Gerencie seus documentos de {currentArea.fullName.toLowerCase()}
              </p>
            </div>
            {canWriteDocs && (
            <Button 
              onClick={() => setUploadModalOpen(true)}
              data-testid="upload-file-button"
              className="bg-white text-slate-900 hover:bg-slate-100 shadow-lg hover:shadow-xl transition-all duration-200 active:scale-95"
            >
              <Upload className="w-4 h-4 mr-2" />
              Enviar Arquivos
            </Button>
            )}
          </div>
        </div>

        <To62ReminderBanner />

        {/* Bento Grid Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {/* Main stat - spans 2 cols on mobile */}
          <Card className="col-span-2 border-0 shadow-card hover:shadow-card-hover transition-all duration-300 animate-fade-in bg-card" style={{ animationDelay: '0.1s' }} data-testid="area-files-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                    Arquivos em {currentArea.name}
                  </p>
                  <p className="text-4xl sm:text-5xl font-extrabold text-foreground mt-3 font-heading">{areaFiles}</p>
                  <div className="flex items-center gap-1 mt-2 text-emerald-600 dark:text-emerald-400">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-sm font-medium">Área ativa</span>
                  </div>
                </div>
                <div className={`w-16 h-16 sm:w-20 sm:h-20 ${currentArea.bgColor} dark:bg-opacity-20 rounded-2xl flex items-center justify-center`}>
                  <AreaIcon className={`w-8 h-8 sm:w-10 sm:h-10 ${currentArea.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card hover:shadow-card-hover transition-all duration-300 animate-fade-in bg-card" style={{ animationDelay: '0.15s' }} data-testid="folders-card">
            <CardContent className="p-5 sm:p-6">
              <div className="flex flex-col h-full">
                <div className="w-10 h-10 bg-amber-50 dark:bg-amber-950/50 rounded-xl flex items-center justify-center mb-3">
                  <Folder className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Pastas</p>
                <p className="text-2xl sm:text-3xl font-bold text-foreground mt-1 font-heading">{areaFolders}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card hover:shadow-card-hover transition-all duration-300 animate-fade-in bg-card" style={{ animationDelay: '0.2s' }} data-testid="storage-card">
            <CardContent className="p-5 sm:p-6">
              <div className="flex flex-col h-full">
                <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center mb-3">
                  <HardDrive className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                </div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Armazenamento</p>
                <p className="text-2xl sm:text-3xl font-bold text-foreground mt-1 font-heading">{formatFileSize(stats?.total_size || 0)}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions & Recent Files - Bento Style */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <Card className="lg:col-span-1 border-0 shadow-card animate-fade-in bg-card" style={{ animationDelay: '0.25s' }} data-testid="quick-actions-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold font-heading">Ações Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="ghost" 
                className="w-full justify-start h-12 hover:bg-accent transition-colors"
                onClick={() => navigate('/documents')}
                data-testid="quick-action-documents"
              >
                <div className={`w-8 h-8 ${currentArea.bgColor} dark:bg-opacity-20 rounded-lg flex items-center justify-center mr-3`}>
                  <AreaIcon className={`w-4 h-4 ${currentArea.color}`} />
                </div>
                <span className="font-medium">Ver Documentos</span>
                <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground" />
              </Button>
              
              {canWriteDocs && (
              <Button 
                variant="ghost" 
                className="w-full justify-start h-12 hover:bg-accent transition-colors"
                onClick={() => setUploadModalOpen(true)}
                data-testid="quick-action-upload"
              >
                <div className="w-8 h-8 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center mr-3">
                  <Upload className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                </div>
                <span className="font-medium">Enviar Pasta</span>
                <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground" />
              </Button>
              )}
              
              <Button 
                variant="ghost" 
                className="w-full justify-start h-12 hover:bg-accent transition-colors"
                onClick={() => navigate('/select-area')}
                data-testid="quick-action-change-area"
              >
                <div className={`w-8 h-8 ${isCongrengacao ? 'bg-maintenance-soft' : 'bg-congregation-soft'} dark:bg-opacity-20 rounded-lg flex items-center justify-center mr-3`}>
                  {isCongrengacao ? (
                    <Wrench className="w-4 h-4 text-maintenance" />
                  ) : (
                    <BookOpen className="w-4 h-4 text-congregation" />
                  )}
                </div>
                <span className="font-medium">Trocar Área</span>
                <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground" />
              </Button>

              {isAdmin() && (
                <Button 
                  variant="ghost" 
                  className="w-full justify-start h-12 hover:bg-accent transition-colors"
                  onClick={() => navigate('/admin/users')}
                  data-testid="quick-action-admin"
                >
                  <div className="w-8 h-8 bg-purple-50 dark:bg-purple-950/50 rounded-lg flex items-center justify-center mr-3">
                    <Users className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <span className="font-medium">Gerenciar Usuários</span>
                  <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground" />
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Recent Files */}
          <Card className="lg:col-span-2 border-0 shadow-card animate-fade-in bg-card" style={{ animationDelay: '0.3s' }} data-testid="recent-files-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-semibold font-heading">Arquivos Recentes</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/documents')}
                className="text-muted-foreground hover:text-foreground"
                data-testid="view-all-files-button"
              >
                Ver todos
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {recentFiles.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FolderOpen className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-foreground font-medium">Nenhum arquivo enviado ainda</p>
                  <p className="text-muted-foreground text-sm mt-1">Comece enviando seu primeiro documento</p>
                  <Button 
                    className="mt-4"
                    onClick={() => setUploadModalOpen(true)}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Enviar arquivo
                  </Button>
                </div>
              ) : (
                <div className="space-y-1">
                  {recentFiles.map((file, index) => {
                    const FileIcon = getFileIcon(file.content_type);
                    return (
                      <div 
                        key={file.id}
                        className="flex items-center gap-4 p-3 rounded-xl hover:bg-accent transition-all duration-200 cursor-pointer group animate-slide-in"
                        style={{ animationDelay: `${0.35 + index * 0.05}s` }}
                        onClick={() => navigate(`/documents?file=${file.id}`)}
                        data-testid={`recent-file-${file.id}`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${currentArea.bgColor} dark:bg-opacity-20 group-hover:scale-110 transition-transform duration-200`}>
                          <FileIcon className={`w-5 h-5 ${currentArea.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate">{file.original_filename}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
                            {file.folder_path && file.folder_path !== '/' && (
                              <>
                                <span className="text-muted">•</span>
                                <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                                  {file.folder_path}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                          <Clock className="w-3 h-3" />
                          {formatDate(file.created_at)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <UploadModal 
        open={uploadModalOpen} 
        onOpenChange={setUploadModalOpen}
        onSuccess={handleUploadSuccess}
        defaultCategory={selectedArea}
        fixedCategory={true}
      />
    </Layout>
  );
}
